/*
 * @Author: changdou.fe@bytedance.com
 * @Date: 2025-08-10 16:22:00
 * @LastEditors: changdou.fe@bytedance.com
 * @LastEditTime: 2025-12-09 21:44:28
 */
import BN from 'bignumber.js';
import request, { AxiosRequestConfig } from '@/lib/axios-config';

export const HL_API_BASE = 'https://api.hyperliquid.xyz';

export interface KData {
  originTime: number; // 时间戳
  time: number; // 时间戳
  open: number; // 开盘
  close: number; // 收盘
  high: number; // 最高
  low: number; // 最低
  volume: number; // 值
}

export interface MetaAndAssetCtxs {
  universe: IUniverse[];
  assetCtxs: IAssetCtxs[];
  tokens: HLTokenInfo[];
}

export interface IUniverse {
  marginTableId: number;
  maxLeverage: number;
  name: string;
  szDecimals: number;
  tickSize: number;
  isDelisted?: boolean;
}

export interface IAssetCtxs {
  funding: string; // 当前资金费率
  openInterest: string; // 总持仓量
  prevDayPx: string; // 前一天收盘价
  dayNtlVlm: string; // 24小时总交易量
  premium: string; // 溢价价格（可能为 null）
  oraclePx: string; // 预言机价格
  markPx: string | number; // 标记价格
  midPx: string; // 中间价格
  impactPxs: string[]; // 影响价格数组（可能为 null）
  dayBaseVlm: string; // 24小时基础货币交易量
}

export interface HLTokenInfo extends IUniverse, IAssetCtxs {
  index: number;
  change: {
    absolute: number;
    percentage: number;
    absoluteText: string;
    percentageText: string;
  };
  realOpenInterest: number;
}

// 通用请求函数 - 使用 axios
export const baseRequest = async (url: string, options: AxiosRequestConfig = {}) => {
  const method = options.method || 'GET';
  const data = options.data;
  const params = options.params;
  let headers: Record<string, string> = method === 'POST' ? { 'Content-Type': 'application/json' } : {};

  const config: AxiosRequestConfig = {
    baseURL: HL_API_BASE,
    method,
    url,
    headers,
    data,
    params,
    timeout: 30000, // 30秒超时
    ...options,
  };

  try {
    const response = await request(config);
    return response;
  } catch (error: any) {
    if (error.response) {
      console.error(`请求失败: ${url}`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // 抛出更详细的错误信息
      throw new Error(`HTTP ${error.response?.status || 'unknown'}: ${error.response?.data?.msg || error.message}`);
    } else {
      console.error(`请求失败: ${url}`, error);
      throw error;
    }
  }
};

// 1.获取HL支持的合约交易的token列表
export const getHLAllTokens = async (): Promise<any> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'metaAndAssetCtxs',
      },
    });
    return data;
  } catch (error) {
    return {};
  }
};

// 2.获取K线图数据
export const getHLKlineData = async (params: { coin: string; interval: string; startTime: number; endTime: number }): Promise<KData[]> => {
  try {
    const data = (await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'candleSnapshot',
        req: params,
      },
    })) as unknown as KData[];
    const klineData = data.map((item: any) => ({
      originTime: item.t,
      time: Number(String(item.t).slice(0, -3)) + 28800,
      open: Number(item.o),
      close: Number(item.c),
      high: Number(item.h),
      low: Number(item.l),
      volume: Number(item.v),
      value: Number(item.v),
    }));
    return klineData;
  } catch (error) {
    return [];
  }
};

// 旧代码
export const hlExchange = async (params: any): Promise<any> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/exchange`, {
      method: 'POST',
      data: params,
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getOrderHistory = async (params: any): Promise<any> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'userFillsByTime',
        reversed: true,
        aggregateByTime: true,
        ...params,
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getMarginTable = async (id: number): Promise<any> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'marginTable',
        id,
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

// 获取永续合约余额
export const getClearinghouseState = async (
  address: string
): Promise<{
  assetPositions: any[];
  marginSummary: any;
  crossMaintenanceMarginUsed: string;
  withdrawable: string;
  crossMarginSummary: {
    accountValue: string;
    totalNtlPos: string;
    totalRawUsd: string;
    totalMarginUsed: string;
  };
}> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'clearinghouseState',
        user: address,
      },
    });
    return data as any;
  } catch (error) {
    return {
      crossMarginSummary: {
        accountValue: '0',
      },
    } as any;
  }
};

export interface IAuthorizationStatus {
  address: string;
  name: string;
  validUntil: number;
}
// 向hyperliquid查询授权状态
export const getAuthorizationStatus = async (address: string): Promise<IAuthorizationStatus[]> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'extraAgents',
        user: address,
      },
    });
    return data as any;
  } catch (error) {
    return [];
  }
};

// export interface IUserFees {
//   feeSchedule: {
//     add: string;
//     cross: string;
//     referralDiscount: string;
//     spotAdd: string;
//     spotCross: string;
//   };
// }

export interface IUserFees {
  baseMakerFeeRate: string;
  baseTakerFeeRate: string;
  makerFeeRate: string;
  takerFeeRate: string;
  hasDiscount: boolean;
}
// 向hyperliquid查询授权状态
export const getUserFees = async (address: string): Promise<IUserFees> => {
  try {
    const data: any = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'userFees',
        user: address,
      },
    });
    if (data?.feeSchedule) {
      // 获取maker和taker费率 接口返回'0.00015'
      const { add: makerFee, cross: takerFee, referralDiscount } = data.feeSchedule;
      const baseMakerFeeRate = (Number(makerFee) * 100).toFixed(4);
      const baseTakerFeeRate = (Number(takerFee) * 100).toFixed(4);
      if (Number(referralDiscount) > 0) {
        // 如果有折扣，则需要减去折扣
        return {
          baseMakerFeeRate: baseMakerFeeRate,
          baseTakerFeeRate: baseTakerFeeRate,
          makerFeeRate: new BN(makerFee)
            .times(new BN(1).minus(new BN(referralDiscount)))
            .times(100)
            .toFixed(4),
          takerFeeRate: new BN(takerFee)
            .times(new BN(1).minus(new BN(referralDiscount)))
            .times(100)
            .toFixed(4),
          hasDiscount: true,
        };
      }
      return {
        // 需要转换为百分比 转换为字符串 并保留4位小数 类似'0.00015' 转换为 '0.0150%'
        baseMakerFeeRate: baseMakerFeeRate,
        baseTakerFeeRate: baseTakerFeeRate,
        makerFeeRate: baseMakerFeeRate,
        takerFeeRate: baseTakerFeeRate,
        hasDiscount: false,
      };
    }
    return data as any;
  } catch (error) {
    return {
      makerFeeRate: '0',
      takerFeeRate: '0',
      baseMakerFeeRate: '0',
      baseTakerFeeRate: '0',
      hasDiscount: false,
    };
  }
};

// 向hyperliquid查询授权状态
export const getMaxBuilderFee = async (address: string, builder: string): Promise<number> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'maxBuilderFee',
        user: address,
        builder,
      },
    });
    return data as any;
  } catch (error) {
    return 0;
  }
};

export const getActiveAssetData = async (address: string, coin: string): Promise<any> => {
  try {
    return await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'activeAssetData',
        user: address,
        coin,
      },
    });
  } catch (error) {
    return {};
  }
};

export const getBestBidAndAsk = async (coin: string): Promise<any> => {
  try {
    const data: any = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'l2Book',
        coin,
      },
    });
    return {
      bestBid: data?.levels?.[0]?.[0]?.px || 0, // 最佳买价（买单第一档）
      bestAsk: data?.levels?.[1]?.[0]?.px || 0, // 最佳卖价（卖单第一档）
      asks: data?.levels?.[1],
      bids: data?.levels?.[0],
    };
  } catch (error) {
    return {
      bestBid: 0,
      bestAsk: 0,
    };
  }
};

export const getOrderBook = async (coin: string): Promise<any> => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'l2Book',
        coin,
      },
    });
    return data;
  } catch (error) {
    return {};
  }
};

export const getFrontendOpenOrders = async (address: string, startTime?: number): Promise<any[]> => {
  try {
    const data: any = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'frontendOpenOrders',
        user: address,
      },
    });
    if (data?.length && startTime) {
      return data.filter((item: any) => item?.timestamp >= startTime);
    }
    return data;
  } catch (error) {
    return [];
  }
};

// 获取历史委托
export const getHistoricalOrders = async (address: string) => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'historicalOrders',
        user: address,
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

// 获取资金费记录
export const getUserFunding = async (params: { user: string; startTime: number; endTime?: number }): Promise<any[]> => {
  try {
    const data: any = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'userFunding',
        ...params,
      },
    });
    if (data?.length) {
      return data.slice().reverse();
    }
    return [];
  } catch (error) {
    return [];
  }
};

// 获取历史委托
export const getUserFills = async (address: string) => {
  try {
    const data = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'userFills',
        user: address,
        aggregateByTime: true,
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

// 根据时间聚合获取用户成交记录
export const getUserFillsByTime = async (address: string, startTime: number) => {
  try {
    const data: any = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'userFillsByTime',
        user: address,
        aggregateByTime: true,
        startTime,
      },
    });
    if (data?.length) {
      return data.slice().reverse();
    }
  } catch (error) {
    return [];
  }
};

// 存款和提款记录
export const getUserNonFundingLedgerUpdates = async (params: { user: string; startTime?: number }): Promise<any> => {
  try {
    const data: any = await baseRequest(`${HL_API_BASE}/info`, {
      method: 'POST',
      data: {
        type: 'userNonFundingLedgerUpdates',
        ...params,
      },
    });
    if (data?.length) {
      // 过滤掉其他状态
      const _data = data.filter((update: any) => ['withdraw', 'deposit'].includes(update?.delta?.type));
      return _data;
    }
    return [];
  } catch (error) {
    return [];
  }
};
